import React, { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

import LoadingProgress from '../../../../../components/LoadingProgress';
import useLoadStreamGroupConfig from './useLoadStreamGroupConfig';
import { useMytaverse } from '../../../../../providers/MytaverseProvider';
import { useNotification } from '../../../../../components/Notification';
import ParticipantsService from '../../../../../services/ParticipantsService';
import { IEvent } from '../../../../../interfaces/event';
import {
  getExternalUrlHash,
  compareTokenHash,
} from '../../../../../helpers/externalUrlHash';
import { clientSpecificEventCarouselTransform } from '../../../../../helpers/eventCarouselSorting';

import SelectEventPageView from './SelectEventPageView';

const { REACT_APP_EVENT_CAROUSEL_HIDE_EXTERNAL_EVENT_TAGS } = process.env;

const SelectEventPage = () => {
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<IEvent[]>([]);

  const { user, token } = useMytaverse();
  const { showNotification, getBadRequestNotification } = useNotification();
  const { pathname } = useLocation();

  const { loadEventStreamGroupConfig } = useLoadStreamGroupConfig();

  const loadEvents = useCallback(async () => {
    setLoading(true);

    const participantEvents =
      await ParticipantsService.getCurrentParticipantEvents();

    if (participantEvents.events.length === 0 && user) {
      const message = `The email address ${
        user.hrData?.personalEmail || ''
      } is not invited to any worlds. Are you sure this is the correct email address?`;
      showNotification(getBadRequestNotification({ message }));

      console.error(message);

      setLoading(false);

      return;
    }

    if (participantEvents.events.length !== 0) {
      const events = await Promise.all(
        participantEvents.events
          .map((participantEvent) => participantEvent.event as IEvent)
          .filter((event) => event.status === 'ACTIVE')
          .map(async (event) => {
            if (event.eventTags && event.eventTags.indexOf('External') > -1) {
              return event;
            }

            try {
              const streamGroupConfig = await loadEventStreamGroupConfig(
                event.eventId,
              );

              return { ...event, streamGroupConfig };
            } catch (e) {
              console.log('StreamGroupConfig:', e);
            }

            return event;
          }),
      );

      setEvents(clientSpecificEventCarouselTransform(events));
    }

    setLoading(false);
  }, [setLoading, setEvents, pathname, user, loadEventStreamGroupConfig]);

  useEffect(() => {
    if (!events.length) {
      loadEvents();
    }

    const externalEventTokenHash = getExternalUrlHash();

    if (externalEventTokenHash) {
      compareTokenHash(token, externalEventTokenHash).then(
        (isTokenHashValid) => {
          if (!isTokenHashValid) {
            const message = 'External event auth token hash is invalid!';
            showNotification(getBadRequestNotification({ message }));
          }
        },
      );
    }
  }, [events, loadEvents]);

  const hideEventTags = (
    REACT_APP_EVENT_CAROUSEL_HIDE_EXTERNAL_EVENT_TAGS || ''
  )
    .split(',')
    .map((i) => i.trim());

  let carouselEvents = events;

  if (hideEventTags.length > 0) {
    // Filter out external events
    carouselEvents = events.filter((event) => {
      if (event.eventTags) {
        return (
          event.eventTags.filter((tag) => !hideEventTags.includes(tag))
            .length === event.eventTags.length
        );
      }

      return true;
    });
  }

  const loop = carouselEvents.length > 2;

  if ((!carouselEvents.length && loading) || loop === null) {
    return <LoadingProgress fullHeight />;
  }

  return <SelectEventPageView events={carouselEvents} loop={loop} />;
};

export default SelectEventPage;
